"use client"

import { Trans } from "@lingui/react/macro"
import {
  Button,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from "@rarible_int/anaconda_ui_components"

import {
  gachaCtaButtonRowPrimaryClassName,
  gachaCtaButtonRowSecondaryClassName,
  gachaDialogFooterClassName,
} from "./gacha-cta-button-styles"
import { normalizeGiftRecipientAddress } from "./gacha-gift-address"

export type GachaGiftSummary = {
  packName: string
  packImage: string
  recipientAddress: string
  quantity: number
  totalPrice: string
}

type GachaSendGiftConfirmationBodyProps = {
  summary: GachaGiftSummary
  busy?: boolean
  onBack?: () => void
  onConfirm?: () => void
}

export function GachaSendGiftConfirmationBody({
  summary,
  busy = false,
  onBack,
  onConfirm,
}: GachaSendGiftConfirmationBodyProps) {
  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-headline font-normal text-2xl leading-tight tracking-tight text-foreground">
          <Trans>Send Gift</Trans>
        </DialogTitle>
      </DialogHeader>

      <div className="flex items-center gap-3 rounded-lg border border-foreground/10 bg-foreground/[0.02] p-3">
        <div className="relative aspect-square w-14 shrink-0 overflow-hidden rounded-md bg-foreground/[0.04]">
          <img
            src={summary.packImage}
            alt=""
            className="h-full w-full object-contain"
          />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground">{summary.packName}</p>
          <p className="mt-1 text-xs text-foreground/60">
            <Trans>Gacha Pack</Trans>
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-foreground/10 p-3 text-sm">
        <GiftSummaryRow
          label={<Trans>Recipient</Trans>}
          value={normalizeGiftRecipientAddress(summary.recipientAddress)}
          mono
        />
        <GiftSummaryRow
          label={<Trans>Quantity</Trans>}
          value={String(summary.quantity)}
        />
        <div className="h-px bg-foreground/10" />
        <GiftSummaryRow
          label={<Trans>Total</Trans>}
          value={summary.totalPrice}
          strong
        />
      </div>

      <DialogFooter className={gachaDialogFooterClassName}>
        <Button
          type="button"
          variant="outline"
          className={gachaCtaButtonRowSecondaryClassName}
          onClick={onBack}
          disabled={busy}
          data-testid="gacha-send-gift-confirmation-back"
        >
          <Trans>Back</Trans>
        </Button>
        <Button
          type="button"
          variant="default"
          className={gachaCtaButtonRowPrimaryClassName}
          onClick={onConfirm}
          disabled={busy}
          data-testid="gacha-send-gift-confirmation-submit"
        >
          {busy ? <Trans>Sending...</Trans> : <Trans>Send Gift</Trans>}
        </Button>
      </DialogFooter>

      <p className="text-xs leading-5 text-foreground/50">
        <Trans>
          Gift transfers cannot be reversed. Confirm the recipient address
          before sending.
        </Trans>
      </p>
    </>
  )
}

function GiftSummaryRow({
  label,
  value,
  mono = false,
  strong = false,
}: {
  label: React.ReactNode
  value: string
  mono?: boolean
  strong?: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="shrink-0 text-foreground/60">{label}</span>
      <span
        className={cn(
          "min-w-0 text-right break-all",
          mono && "font-mono",
          strong && "text-base text-brand",
        )}
      >
        {value}
      </span>
    </div>
  )
}
